import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk";

let backgroundStart: DOMHighResTimeStamp | null = null;
let totalBackgroundTime = 0;
let start = performance.now();

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    // L’onglet passe en arrière-plan → on note l’heure de départ
    backgroundStart = performance.now();
  } else if (document.visibilityState === "visible" && backgroundStart !== null) {
    // L’onglet redevient visible → on ajoute la durée écoulée
    totalBackgroundTime += performance.now() - backgroundStart;
    backgroundStart = null;
  }
});

export const getTotalBackground = () => {
  // Si on est encore en background, on ajoute la durée actuelle
  let total = totalBackgroundTime;
  if (document.visibilityState === "hidden" && backgroundStart !== null) {
    total += performance.now() - backgroundStart;
  }
  return total; 
}

export const cpuBenchmark = (iterations = 100000000) => {
  const start = performance.now();
  let result = 0;
  for (let i = 0; i < iterations; i++) {
    result += Math.sqrt(i) * Math.sin(i);
  }
  const end = performance.now();
  return {
    iterations,
    timeMs: end - start,
    score: iterations / (end - start) // plus le score est élevé, plus le CPU est rapide
  };
}


export const collectDiagnostics = async (container?: MeetingContainerAPI, extraData?: any) => {
  const diagnostics: any = {};

  // --- 1. Infos navigateur ---
  diagnostics.browser = {
    userAgent: navigator.userAgent,
    userAgentData: (navigator as any).userAgentData,
    platform: navigator.platform,
    language: navigator.language,
    vendor: navigator.vendor,
    cores: navigator.hardwareConcurrency || null,
    deviceMemory: (navigator as any).deviceMemory || null,
    maxTouchPoints: navigator.maxTouchPoints || 0
  };

  // --- 2. Infos écran / affichage ---
  diagnostics.screen = {
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    screen: {
      availHeight:  window.screen?.availHeight,
      availWidth:  window.screen?.availWidth,
      colorDepth:  window.screen?.colorDepth,
      height:  window.screen?.height,
      pixelDepth:  window.screen?.pixelDepth,
      width:  window.screen?.width,
    }
  };

  // --- 3. Infos réseau ---
  diagnostics.network = {
    online: navigator.onLine,
    url: window.location.href,
    connection: (navigator as any).connection
      ? {
          effectiveType: (navigator as any).connection.effectiveType,
          downlink: (navigator as any).connection.downlink,
          rtt: (navigator as any).connection.rtt,
          type: (navigator as any).connection.type
        }
      : null
  };

  // --- Performance navigation ---
  diagnostics.performance = {
    cpuBenchmark: cpuBenchmark(),
  };

  if (performance.getEntriesByType("navigation").length > 0) {
    const nav: any = performance.getEntriesByType("navigation")[0];
    diagnostics.performance.navigation = {
      startTime: nav.startTime,
      domContentLoaded: nav.domContentLoadedEventEnd,
      loadEventEnd: nav.loadEventEnd,
      responseStart: nav.responseStart,
      responseEnd: nav.responseEnd,
      fetchStart: nav.fetchStart,
      redirectStart: nav.redirectStart,
      redirectEnd: nav.redirectEnd,
      domainLookupStart: nav.domainLookupStart,
      domainLookupEnd: nav.domainLookupEnd,
      connectStart: nav.connectStart,
      connectEnd: nav.connectEnd,
      requestStart: nav.requestStart
    };
  }

  // --- Ressources ---
  /*diagnostics.performance.resources = performance.getEntriesByType("resource").map(res => ({
    name: res.name,
    type: res.initiatorType,
    startTime: res.startTime,
    duration: res.duration,
    transferSize: res.transferSize,
    encodedBodySize: res.encodedBodySize,
    decodedBodySize: res.decodedBodySize
  }));*/

  // --- Peinture ---
  diagnostics.performance.paint = performance.getEntriesByType("paint").map(p => ({
    name: p.name,
    startTime: p.startTime
  }));

  // --- Long tasks ---
  diagnostics.performance.longTasks = performance.getEntriesByType("longtask").map(task => ({
    name: task.name,
    startTime: task.startTime,
    duration: task.duration
  }));

  // --- Mémoire (Chrome uniquement) ---
  diagnostics.performance.memory = (performance as any).memory
    ? {
        jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit,
        totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
        usedJSHeapSize: (performance as any).memory.usedJSHeapSize
      }
    : null;


  const duration = performance.now() - start;
  diagnostics.time = {
    localISO: new Date().toISOString(),
    duration
  };

  try {
    const devices= await navigator.mediaDevices.enumerateDevices();
    diagnostics.mediaDevices = devices;
  } catch(err) {
    diagnostics.mediaDevices = { err: JSON.stringify(err) };
  }

  diagnostics.permissions = {};

  try {
    const microphone = await navigator.permissions.query({ name : "camera" as any });
    diagnostics.permissions.microphone = microphone.state;
  } catch(err) {
    diagnostics.permissions.microphone = { err: JSON.stringify(err) };
  }

  try {
    const camera = await navigator.permissions.query({ name : "microphone" as any });
    diagnostics.permissions.camera = camera.state;
  } catch(err) {
    diagnostics.permissions.camera = { err: JSON.stringify(err) };
  }

  diagnostics.videos = {
    emissions: [],
    receptions: [],
  }
  diagnostics.snapshot = {};
  if (container) {
    const state = container.store?.getState();
    const promises: Promise<any>[] = [];
    const getRTCStatsJSON = async (peerConnection: RTCPeerConnection) => {
      const stats = await peerConnection.getStats();
      const statsObj: any = {};
    
      stats.forEach((report) => {
        statsObj[report.id] = report; // chaque rapport est déjà un objet sérialisable
      });
    
      return statsObj;
    }
    if (state) {
      state.emissionSessions.forEach((s) => {
        promises.push(new Promise(async (resolve) => {
          try {
            diagnostics.videos.emissions.push({
              rtc: {
                connectionState: s.rtcPeerConnection?.connectionState,
                iceConnectionState: s.rtcPeerConnection?.iceConnectionState,
                stats: s.rtcPeerConnection ? await getRTCStatsJSON(s.rtcPeerConnection) : undefined,
                senders: s.rtcPeerConnection?.getSenders()?.map((s) => ({
                  track: {
                    kind: s.track?.kind,
                    label: s.track?.label,
                    enabled: s.track?.enabled,
                    readyState: s.track?.readyState,
                    capatilities: s.track?.getCapabilities(),
                    settings: s.track?.getSettings(),
                    constraints: s.track?.getConstraints(),
                  },
                  params: s.getParameters()
                }))
              },
              socket: {
                connected: s.socketSession?.connected
              },
              state: s.state,
            })
          } catch(err) {}
          resolve(null);
        }))
      });
 
      for (let s of state.receptionSessions) {
        promises.push(new Promise(async (resolve) => {
          try {
            diagnostics.videos.receptions.push({
              rtc: {
                connectionState: s.rtcPeerConnection?.connectionState,
                iceConnectionState: s.rtcPeerConnection?.iceConnectionState,
                stats: s.rtcPeerConnection ? await getRTCStatsJSON(s.rtcPeerConnection) : undefined,
              },
              media: s.incomingStream?.getTracks()?.map((t) => ({
                kind: t.kind,
                enabled: t.enabled,
                readyState: (t as any).readyState
              })),
              socket: {
                connected: s.socketSession?.connected
              },
              state: s.state,
              video: s.videoIsEnabled,
              audio: s.audioIsEnabled
            })
          } catch(err) {}
          resolve(null);
        }))
      }
      diagnostics.snapshot.meeting = state.meeting;
      diagnostics.snapshot.session = state.session;
    }
    await Promise.allSettled(promises);
  }
  

  diagnostics.extraData = extraData;

  diagnostics.backgroundTime = {
    total: getTotalBackground(),
    percentage: (getTotalBackground()/duration*100).toFixed(1)
  }

  return diagnostics;
}
